import { inject, Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import Keycloak from 'keycloak-js';

import { IAuthProvider } from '../auth-provider.interface';
import {
    clearAllStorage,
    refreshTokenSignal,
    tokenSignal
} from '../../constants/auth-state';
import { environment } from '../../../../environments/environment';
import { AuthLoginOptions } from '../auth.types';
import { FORCE_INTERACTIVE_LOGIN_STORAGE_KEY } from '../auth-session.constants';

@Injectable({
    providedIn: 'root'
})
export class KeycloakAuthProvider implements IAuthProvider {

    private readonly keycloak = inject(Keycloak);
    private sessionEndInProgress = false;
    private logoutInProgress = false;

    async initialize(): Promise<void> {
        this.bindSessionHandlers();

        const shouldSkipSilentSso = this.shouldSkipSilentSsoCheck();
        const storedToken = tokenSignal();
        const storedRefreshToken = refreshTokenSignal();

        await this.keycloak.init({
            ...(shouldSkipSilentSso
                ? {}
                : { onLoad: 'check-sso' as const }),
            ...(storedToken
                ? { token: storedToken }
                : {}),
            ...(storedRefreshToken
                ? { refreshToken: storedRefreshToken }
                : {}),
            checkLoginIframe: true,
            pkceMethod: 'S256',
            responseMode: 'query',
            redirectUri: this.getCurrentRedirectUri(),
            silentCheckSsoRedirectUri: this.getSilentCheckSsoRedirectUri(),
            silentCheckSsoFallback: false,
        });

        if (this.keycloak.token) {
            tokenSignal.set(this.keycloak.token);
        }

        if (this.keycloak.refreshToken) {
            refreshTokenSignal.set(this.keycloak.refreshToken);
        }
    }

    async login(options?: AuthLoginOptions): Promise<void> {
        this.clearInteractiveLoginMarker();

        await this.keycloak.login({
            redirectUri: options?.redirectUri || this.getCurrentRedirectUri(),
            ...(options?.prompt
                ? { prompt: options.prompt }
                : {}),
            ...(this.getIdentityProviderHint()
                ? { idpHint: this.getIdentityProviderHint() }
                : {})
        });
    }

    async logout(): Promise<void> {
        this.logoutInProgress = true;
        this.setInteractiveLoginMarker();
        clearAllStorage();
        this.keycloak.clearToken();
        tokenSignal.set(null);

        try {
            this.navigateToUrl(
                this.keycloak.createLogoutUrl({
                    redirectUri: this.getPostLogoutRedirectUri()
                })
            );
        } catch (error) {
            this.logoutInProgress = false;
            throw error;
        }
    }

    clearSession(): void {
        this.keycloak.clearToken();
        tokenSignal.set(null);
        refreshTokenSignal.set(null);
    }

    isAuthenticated(): boolean {
        return !!this.keycloak.authenticated;
    }

    getAccessToken(): Observable<string> {
        return from(
            (async () => {
                if (!this.keycloak.authenticated) {
                    return this.keycloak.token ?? '';
                }

                if (this.keycloak.refreshToken) {
                    await this.keycloak.updateToken(30);
                }

                if (this.keycloak.token) {
                    tokenSignal.set(this.keycloak.token);
                }

                if (this.keycloak.refreshToken) {
                    refreshTokenSignal.set(this.keycloak.refreshToken);
                }

                return this.keycloak.token ?? '';
            })()
        );
    }
    getCurrentUserEmail(): string | null {
        return this.keycloak.tokenParsed?.['email'] as string ?? null;
    }

    getCurrentUserRoles(): string[] {
        return this.keycloak.realmAccess?.roles ?? [];
    }

    private getCurrentRedirectUri(): string {
        const { origin, pathname, search } = window.location;

        // Keep the active route so refresh/login returns the user to the same page.
        return `${origin}${pathname}${search}`;
    }

    private getPostLogoutRedirectUri(): string {
        const configuredRedirectUri = environment.auth.keycloak.postLogoutRedirectUri?.trim();
        const appOrigin = window.location.origin;

        if (!configuredRedirectUri || configuredRedirectUri === appOrigin || configuredRedirectUri === `${appOrigin}/`) {
            return `${appOrigin}/loggedout`;
        }

        return configuredRedirectUri;
    }

    private getSilentCheckSsoRedirectUri(): string {
        return `${window.location.origin}/silent-check-sso.html`;
    }

    private getIdentityProviderHint(): string | undefined {
        const keycloakConfig = environment.auth?.keycloak as { idpHint?: string } | undefined;
        return keycloakConfig?.idpHint || undefined;
    }

    private shouldSkipSilentSsoCheck(): boolean {
        return (
            typeof window !== 'undefined' &&
            window.location.pathname === '/loggedout' &&
            window.localStorage.getItem(FORCE_INTERACTIVE_LOGIN_STORAGE_KEY) === 'true'
        );
    }

    private setInteractiveLoginMarker(): void {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        window.localStorage.setItem(FORCE_INTERACTIVE_LOGIN_STORAGE_KEY, 'true');
    }

    private clearInteractiveLoginMarker(): void {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }

        window.localStorage.removeItem(FORCE_INTERACTIVE_LOGIN_STORAGE_KEY);
    }

    private bindSessionHandlers(): void {
        this.keycloak.onAuthLogout = () => {
            void this.handleSessionEnded();
        };

        this.keycloak.onAuthRefreshError = () => {
            void this.handleSessionEnded();
        };

        this.keycloak.onTokenExpired = () => {
            void this.refreshTokenSilently();
        };
    }

    private async handleSessionEnded(): Promise<void> {
        if (this.sessionEndInProgress || this.logoutInProgress) {
            return;
        }

        this.sessionEndInProgress = true;
        clearAllStorage();
        this.clearSession();

        try {
            this.replaceCurrentLocation(this.getPostLogoutRedirectUri());
        } finally {
            this.sessionEndInProgress = false;
        }
    }

    private async refreshTokenSilently(): Promise<void> {
        if (this.logoutInProgress) {
            return;
        }

        try {
            if (!this.keycloak.authenticated || !this.keycloak.refreshToken) {
                return;
            }

            const refreshed = await this.keycloak.updateToken(30);

            if ((refreshed || this.keycloak.token) && this.keycloak.token) {
                tokenSignal.set(this.keycloak.token);
            }

            if (this.keycloak.refreshToken) {
                refreshTokenSignal.set(this.keycloak.refreshToken);
            }
        } catch {
            await this.handleSessionEnded();
        }
    }

    private navigateToUrl(url: string): void {
        window.location.assign(url);
    }

    private replaceCurrentLocation(url: string): void {
        window.location.replace(url);
    }
}
